/**
 * Table Diff / Row-Count Comparison — a top-level admin tab. Compares per-table
 * row counts + sizes between TWO databases (same connection or two different
 * connections). Read-only.
 *
 * Estimate mode reads information_schema.TABLES (instant, but TABLE_ROWS is an
 * engine ESTIMATE for InnoDB). Exact mode runs SELECT COUNT(*) per table
 * (accurate, slower). Tables present on only one side are flagged. Props: { active }.
 */
import { createSignal, createResource, createMemo, For, Show } from 'solid-js';
import { api } from '../lib/api.js';
import { formatBytes } from '../lib/format.js';
import { compareTables } from '../lib/tablecompare.js';
import { toast } from '../lib/toast.js';

const SYSTEM_DBS = ['mysql', 'information_schema', 'performance_schema', 'sys'];
const STATUS_LABEL = { same: 'Same', diff: 'Differs', 'only-a': 'Only A', 'only-b': 'Only B' };

function deltaCell(n) {
  if (n === 0) return <span class="muted">0</span>;
  const sign = n > 0 ? '+' : '';
  return <span class={n > 0 ? 'tc-delta-up' : 'tc-delta-down'}>{sign}{n.toLocaleString()}</span>;
}

export default function TableComparePanel() {
  const [conns] = createResource(async () => {
    const { connections } = await api.get('/api/connections');
    return connections ?? [];
  });

  const makeSide = () => {
    const [connId, setConnId] = createSignal('');
    const [dbList, setDbList] = createSignal(null);
    const [loading, setLoading] = createSignal(false);
    const [db, setDb] = createSignal('');
    const load = async (id) => {
      if (!id) { setDbList(null); return; }
      setLoading(true);
      try {
        const { databases } = await api.get(`/api/connections/${id}/databases`);
        setDbList((databases ?? []).map((d) => d.name).filter((n) => !SYSTEM_DBS.includes(n)));
      } catch (e) { setDbList([]); toast.error(e.message); }
      finally { setLoading(false); }
    };
    return { connId, setConnId, dbList, loading, db, setDb, load };
  };
  const A = makeSide();
  const B = makeSide();

  const [mode, setMode] = createSignal('estimate'); // 'estimate' | 'exact'
  const [comparing, setComparing] = createSignal(false);
  const [result, setResult] = createSignal(null);
  const [statusFilter, setStatusFilter] = createSignal('');
  const [err, setErr] = createSignal('');

  const onConn = (side, id) => { side.setConnId(id); side.setDb(''); setResult(null); side.load(id); };

  const compare = async () => {
    if (!A.connId() || !B.connId()) { setErr('Pick a connection on both sides.'); return; }
    if (!A.db() || !B.db()) { setErr('Pick a database on both sides.'); return; }
    setErr(''); setComparing(true); setResult(null);
    try {
      const r = await compareTables(A.connId(), A.db(), B.connId(), B.db(), mode());
      setResult(r);
      const s = r.summary;
      if (s.diff || s.onlyA || s.onlyB) toast.info(`${s.diff} differ · ${s.onlyA + s.onlyB} table(s) on one side only.`);
      else toast.success('Both databases match table-for-table. ✓');
    } catch (e) { setErr(e.message); }
    finally { setComparing(false); }
  };

  const summary = () => result()?.summary || {};
  const rows = () => result()?.rows || [];
  const visible = createMemo(() => {
    const f = statusFilter();
    return f ? rows().filter((r) => r.status === f) : rows();
  });
  const connName = (id) => conns()?.find((c) => String(c.id) === String(id))?.name || '';

  return (
    <div class="qc-panel tc-panel">
      <div class="qc-head">
        <div>
          <h2>Table Comparison</h2>
          <p class="muted">Compare per-table row counts &amp; sizes between two databases (same or different connections).</p>
        </div>
      </div>

      <div class="tc-sides">
        <SidePicker side={A} label="A" cls="a" conns={conns()} onConn={(id) => onConn(A, id)} />
        <div class="tc-vs-sep">vs</div>
        <SidePicker side={B} label="B" cls="b" conns={conns()} onConn={(id) => onConn(B, id)} />
      </div>

      <div class="tc-controls">
        <div class="col-expand-modes tc-modes">
          <button class="col-expand-mode" classList={{ active: mode() === 'estimate' }} onClick={() => setMode('estimate')}>
            Estimate
          </button>
          <button class="col-expand-mode" classList={{ active: mode() === 'exact' }} onClick={() => setMode('exact')}>
            Exact (COUNT *)
          </button>
        </div>
        <span class="muted tc-mode-note">
          {mode() === 'estimate'
            ? 'Fast — information_schema; InnoDB row counts are approximate.'
            : 'Accurate — one COUNT(*) per table; slower on big tables.'}
        </span>
        <div class="spacer" />
        <button class="btn" onClick={compare} disabled={comparing() || !A.connId() || !B.connId()}>
          {comparing() ? '⏳ Comparing…' : '⇄ Compare'}
        </button>
      </div>

      <Show when={err()}><div class="form-error" style="margin:8px 0;">{err()}</div></Show>

      <Show when={result()}>
        <div class="aa-summary tc-summary">
          <span class="muted">{summary().tables} tables ·</span>
          <Show when={summary().same}><span class="tc-pill tc-pill-same">{summary().same} same</span></Show>
          <Show when={summary().diff}><span class="tc-pill tc-pill-diff">{summary().diff} differ</span></Show>
          <Show when={summary().onlyA}><span class="tc-pill tc-pill-only">{summary().onlyA} only A</span></Show>
          <Show when={summary().onlyB}><span class="tc-pill tc-pill-only">{summary().onlyB} only B</span></Show>
        </div>

        <div class="ih-chips tc-chips">
          <button class={`day-chip ${statusFilter() === '' ? 'on' : ''}`} onClick={() => setStatusFilter('')}>All ({rows().length})</button>
          <For each={['diff', 'only-a', 'only-b', 'same']}>
            {(s) => {
              const n = () => rows().filter((r) => r.status === s).length;
              return (
                <Show when={n() > 0}>
                  <button class={`day-chip ${statusFilter() === s ? 'on' : ''}`} onClick={() => setStatusFilter(s)}>
                    {STATUS_LABEL[s]} ({n()})
                  </button>
                </Show>
              );
            }}
          </For>
        </div>

        <div class="tc-table-wrap">
          <table class="tc-table">
            <thead>
              <tr>
                <th>Table</th>
                <th class="tc-num">Rows A</th>
                <th class="tc-num">Rows B</th>
                <th class="tc-num">Δ rows</th>
                <th class="tc-num">Size A</th>
                <th class="tc-num">Size B</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <For each={visible()}>
                {(r) => (
                  <tr class={`tc-row tc-row-${r.status}`}>
                    <td class="mono">{r.name}</td>
                    <td class="tc-num">{r.a ? r.a.rows.toLocaleString() : '—'}</td>
                    <td class="tc-num">{r.b ? r.b.rows.toLocaleString() : '—'}</td>
                    <td class="tc-num">{r.a && r.b ? deltaCell(r.rowsDelta) : <span class="muted">—</span>}</td>
                    <td class="tc-num">{r.a ? formatBytes(r.a.size) : '—'}</td>
                    <td class="tc-num">{r.b ? formatBytes(r.b.size) : '—'}</td>
                    <td><span class={`tc-badge tc-badge-${r.status}`}>{STATUS_LABEL[r.status]}</span></td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={!visible().length}><div class="muted" style="padding:10px;">No tables in this filter.</div></Show>
        </div>

        <p class="cr-footnote muted">
          Comparing <strong>{connName(A.connId())} · {A.db()}</strong> (A) ↔ <strong>{connName(B.connId())} · {B.db()}</strong> (B).
          <Show when={result().mode === 'estimate'}> Row counts are engine estimates — switch to <strong>Exact</strong> for precise figures.</Show>
        </p>
      </Show>
    </div>
  );
}

function SidePicker(props) {
  const s = props.side;
  return (
    <div class={`tc-side tc-side-${props.cls}`}>
      <div class="tc-side-label">Database {props.label}</div>
      <select class="input" value={s.connId()} onChange={(e) => props.onConn(e.currentTarget.value)}>
        <option value="">— connection —</option>
        <For each={props.conns ?? []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
      </select>
      <select class="input" value={s.db()} onChange={(e) => s.setDb(e.currentTarget.value)}
              disabled={s.loading() || !s.dbList()}>
        <option value="">{s.loading() ? 'loading…' : '— database —'}</option>
        <For each={s.dbList() ?? []}>{(n) => <option value={n}>{n}</option>}</For>
      </select>
    </div>
  );
}
